'use client'

// React Imports
import { useState, useEffect, useMemo, useCallback } from 'react'

// MUI Imports
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import LaunchIcon from '@mui/icons-material/Launch'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Avatar from '@mui/material/Avatar'

// Third-party Imports
import { Icon } from '@iconify/react'
import { createColumnHelper } from '@tanstack/react-table'

// Component Imports
import PremiumModuleWrapper from '../../../../Task/Components/common/PremiumModuleWrapper'
import OptionMenu from '../../../../../components/common/Template_Layouts/@core/components/option-menu'
// import { EntityEdit } from '../../EntityEdit'
import EntityEdit from '../../../../../components/common/EntityEdit/EntityEdit'
import Loader from '../../../../../components/Loader/Loader'

// Util Imports
import { getInitials } from '../../../../../utils/helpers'
import { getStatusDescriptor, formatDate } from '../../../../Task/Components/common/premiumUtils'
import API_ENDPOINTS from '../../../../../config/apiEndpoints'
import ScoreCard from '../../../../../components/common/ScoreCard/ScoreCard'

const API_SERVICE_BASE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
const columnHelper = createColumnHelper()

const FollowUpListWrapper = ({
    onAction, // Main handler for all actions (SMS, Mail, etc.)
    onViewDetail,
    onViewLead,
    // New Props for Relocated Filters
    filters,
    handleStatusFilter,
    handleShowAll,
    searchTerm,
    setSearchTerm,
    onExport,
    onCustomDownload,
    viewMode,
    setViewMode,
    // Optimized Props from FollowUpModule
    data,
    totalRecords,
    loading,
    currentPage,
    itemsPerPage,
    onPageChange,
    stats,
    isInitialLoad,
    crmOperationPerms,
    isHiddenField = () => false
}) => {
    // States (Removed local paging/loading as they are now props)
    const [showEntityEdit, setShowEntityEdit] = useState(false)
    const [selectedEntity, setSelectedEntity] = useState(null)
    const [isSavingEntity, setIsSavingEntity] = useState(false)

    const handleEntitySave = async (formData) => {
        try {
            setIsSavingEntity(true)
            const rootUserId = localStorage.getItem('userId') || "34594"
            const rootParentId = "34594"

            // Precompute tomorrow date for FollowupDate
            const tomorrowDate = new Date()
            tomorrowDate.setDate(tomorrowDate.getDate() + 1)
            const tomorrowStr = tomorrowDate.toISOString().split('T')[0]

            // Custom Field Mapping (Mimicking logic from old EntityEdit.jsx)
            const customFieldMap = {
                "amount": "8844", "Service": "8845", "Charges": "8846", "Bank_Name_okl": "8847",
                "Bill_Amount": "8848", "Due_Date": "8849", "Card_Status": "8850", "Card_Type": "8851",
                "Bank_Name_okl_2": "8868", "Bill_Amount_2": "8869", "Due_Date_2": "8870", "Card_Status_2": "8871",
                "Card_Type_2": "8872", "Bank_Name_okl_3": "8873", "Bill_Amount_3": "8874", "Due_Date_3": "8875",
                "Card_Status_3": "8876", "Card_Type_3": "8877", "ClientID": "2686", "DOB": "1237",
                "NAME": "2503", "StudyLevel": "5291", "Course": "5292", "Centre_For_Education": "5293",
                "whatsaap_response": "2796", "Response": "2795", "User_Code": "6276",
                "Company_Incorporation_Date": "7580", "MCA_Registered_City": "7581", "Equity_Percentage": "7583",
                "Mandate_Start_Date": "7585", "Mandate_End_Date": "7586", "OPE_Preapproval": "7587",
                "Business_Referral_Mandate": "7588", "Addendum": "7589", "Project_Name": "7620",
                "is_Adult": "7692", "Locate_Our_Office": "8153", "test121": "8167",
                "number_of_users": "8244", "rrtr": "8580", "dytdutdtjg": "8629",
                "pending_amount": "8630", "warranty_valid_till": "8780", "warranty_start_date": "8781",
                "product": "8066", "Select_Option": "8899", "DATE_TIME": "8903",
                "monthly_EMI": "8994", "Demo_date": "9513", "new_test": "10056",
                "trest": "10365", "testing09099": "10528", "upload_doc": "10545",
                "official_address": "8142", "test": "8171", "KW_Requirement": "10620",
                "gender": "10546"
            }

            const formatDateForAPI = (dateString, useHyphen = true) => {
                if (!dateString) return ""
                try {
                    const date = new Date(dateString)
                    if (isNaN(date.getTime())) return dateString
                    const format = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                    return useHyphen ? format.replace(/ /g, '-') : format
                } catch (e) { return "" }
            }

            const lstCustomField = Object.entries(customFieldMap).map(([key, id]) => {
                let value = formData[key]?.toString() || ""
                // Default handling as per old component
                if (id === "8849" && !value) value = "01 Jan 1900"
                if (id === "1237" && !value) value = "28-Aug-2023"
                if (id === "8849" && value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    value = formatDateForAPI(value, false)
                }
                return { FieldId: id, FieldValue: value }
            })

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    parentId: parseInt(rootParentId || 34594),
                    userId: parseInt(rootUserId || 34594),
                    LeadId: parseInt((formData.LeadId || selectedEntity?.leadId || selectedEntity?.enquiryId || "0").toString(), 10),
                    LeadNo: formData.LeadNo?.toString() || "",
                    PersonName: formData.PersonName || "",
                    CompanyName: formData.CompanyName || "",
                    MobileNo: formData.MobileNo || "",
                    MobileNo1: "",
                    MobileNo2: "",
                    EmailID: formData.EmailId || "",
                    EmailID1: "",
                    EmailID2: "",
                    Country: formData.CountryId || "",
                    State: formData.StateId || "",
                    City: formData.CityId || "",
                    PinCode: formData.Pincode?.toString() || "0",
                    ResidentialAddress: formData.ResidentialAddress || "",
                    OfficialAddress: formData.OfficialAddress || "",
                    SourceName: formData.SourceId || "0",
                    SourceId: 0,
                    MediumName: formData.MediumId || "0",
                    MediumId: 0,
                    CampaignName: formData.CampaignId || "0",
                    CampaignId: 0,
                    InitialRemarks: formData.InitialRemarks || "",
                    InitialRemarkId: 0,
                    WhereAddress: "",
                    WhereLatitude: "",
                    WhereLongitude: "",
                    LeadTags: formData.Tags || "",
                    PersonImage: "",
                    lstCustomField: lstCustomField
                }
            }

            const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '')
            const SaveEditUrl = `${NEWV3_BASE_URL}/UserCRM/EditLead`
            const response = await fetch(SaveEditUrl, {
                method: 'POST',
                headers: { "Content-Type": "application/json; charset=utf-8" },
                body: JSON.stringify(payload)
            })

            if (response.ok) {
                setShowEntityEdit(false)
                // Optionally trigger a refresh of the data
                onPageChange && onPageChange(currentPage)
            } else {
                console.error("Failed to save entity changes")
            }
        } catch (error) {
            console.error("Error saving entity:", error)
        } finally {
            setIsSavingEntity(false)
        }
    }

    const getAgentAvatar = (avatar, name) => {
        return (
            <Avatar
                src={avatar ? (avatar.startsWith('http') ? avatar : `${API_SERVICE_BASE}/${avatar}`) : undefined}
                sx={{
                    width: 36,
                    height: 36,
                    bgcolor: '#f1f5f9', // Light gray/blue background from image
                    color: '#64748b',   // Gray icon color from image
                    border: '1px solid #e2e8f0'
                }}
            >
                <Icon icon="ri:user-3-line" fontSize={18} />
            </Avatar>
        );
    }

    const getAvatar = params => {
        const { avatar, name, threshold } = params
        const colorSchemes = [
            { bg: '#fdf2f8', text: '#db2777' }, // Pink
            { bg: '#f0fdf4', text: '#16a34a' }, // Green
            { bg: '#eff6ff', text: '#2563eb' }, // Blue
            { bg: '#f5f3ff', text: '#7c3aed' }, // Purple
            { bg: '#fff7ed', text: '#ea580c' }, // Orange
            { bg: '#fffbeb', text: '#d97706' }, // Amber
            { bg: '#ecfeff', text: '#0891b2' }, // Cyan
            { bg: '#f0f9ff', text: '#0284c7' }, // Sky
            { bg: '#fdf4ff', text: '#c026d3' }, // Fuchsia
            { bg: '#f8fafc', text: '#475569' }, // Slate
        ]
        const stringHash = (str) => {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                hash = str.charCodeAt(i) + ((hash << 5) - hash);
            }
            return Math.abs(hash);
        };

        const scheme = colorSchemes[stringHash(name || '') % colorSchemes.length] || colorSchemes[2]

        return (
            <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                {/* Score badge — top-left corner, on top of avatar */}
                <span style={{
                    position: 'absolute',
                    top: '-6px',
                    left: '-6px',
                    zIndex: 2,
                    backgroundColor: '#2563eb',
                    color: '#fff',
                    fontSize: '10px',
                    fontWeight: 700,
                    lineHeight: 1,
                    minWidth: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 3px',
                    border: '1.5px solid #fff',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.18)'
                }}>
                    {threshold ?? 0}
                </span>
                {/* Avatar circle */}
                <div style={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    backgroundColor: scheme.bg,
                    color: scheme.text,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '15px',
                    overflow: 'hidden',
                    border: `1.5px solid ${scheme.text}22`,
                    flexShrink: 0
                }}>
                    {avatar ? (
                        <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        getInitials(name || 'Unknown')
                    )}
                </div>
            </div>
        )
    }

    const columns = useMemo(() => [
        columnHelper.accessor('taskTitle', {
            header: 'FOLLOW UP',
            cell: ({ row }) => {
                const statusLabel = getStatusDescriptor(
                    row.original.taskStatus,
                    row.original.endDate,
                    row.original.completedDate,
                    row.original.taskTitle,
                    row.original.startDate,
                    row.original.statusLabel,
                    row.original.outcomeName,
                    row.original.createdDate
                )

                let chipColor = 'info'
                const lowerLabel = statusLabel.toLowerCase()
                if (lowerLabel.includes('overdue')) chipColor = 'error'
                else if (lowerLabel.includes('due today')) chipColor = 'success'
                else if (lowerLabel.includes('due in') || lowerLabel.includes('scheduled')) chipColor = 'warning'
                else if (lowerLabel.includes('completed') || lowerLabel.includes('converted')) chipColor = 'success'
                else chipColor = 'info'

                return (
                    <div className='flex items-center gap-3'>
                        <div
                            className='flex items-center justify-center rounded-full flex-shrink-0'
                            style={{
                                width: '26px',
                                height: '26px',
                                color:
                                    chipColor === 'error' ? '#ef4444' :
                                        chipColor === 'warning' ? '#f59e0b' :
                                            chipColor === 'success' ? '#22c55e' :
                                                chipColor === 'info' ? '#3b82f6' : '#94a3b8'
                            }}
                        >
                            <svg style={{ width: '26px', height: '26px', fill: 'currentColor' }} viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                                <path d="M485.9 696.6c-6.4 0-12.5-3.6-15.6-9.5-29.8-57.6-66.5-62.7-66.9-62.7-9.7-1-16.7-9.5-15.8-19.2 1-9.7 9.5-16.7 19.2-15.8 1.5 0.1 15.9 1.8 34.6 13.2 13.5 8.2 25.8 19.1 36.8 32.7 4.2-14.2 10-30.7 17.9-47.4 28.9-61.5 71-95.4 121.7-98 9.7-0.5 18 7 18.4 16.7 0.5 9.7-7 18-16.7 18.4-37.6 2-68.5 28.1-91.8 77.9-18.5 39.4-24.7 78.6-24.7 78.9-1.2 7.5-7 13.4-14.5 14.7-0.7 0-1.7 0.1-2.6 0.1z m0 0"></path>
                                <path d="M758.2 415.2H265.8c-9.7 0-17.5-7.8-17.5-17.5s7.8-17.5 17.5-17.5h492.5c9.7 0 17.5 7.8 17.5 17.5 0 9.6-7.9 17.5-17.6 17.5z m0 0"></path>
                                <path d="M722.6 214.2h-20.4V169c0-9.7-7.8-17.5-17.5-17.5s-17.5 7.8-17.5 17.5v45.2H356.8V169c0-9.7-7.8-17.5-17.5-17.5s-17.5 7.8-17.5 17.5v45.2h-20.4c-77.3 0-140.2 62.9-140.2 140.3v353.9c0 77.3 62.9 140.2 140.2 140.2h421.2c77.3 0 140.2-62.9 140.2-140.2v-354c0.1-77.3-62.8-140.2-140.2-140.2z m105.2 494.1c0 58-47.1 105.2-105.2 105.2H301.4c-58 0-105.2-47.1-105.2-105.2V354.4c0-58 47.1-105.2 105.2-105.2h20.4v45.2c0 9.7 7.8 17.5 17.5 17.5s17.5-7.8 17.5-17.5v-45.2h310.3v45.2c0 9.7 7.8 17.5 17.5 17.5s17.5-7.8 17.5-17.5v-45.2h20.4c58 0 105.2 47.1 105.2 105.2v353.9z m0 0"></path>
                            </svg>
                        </div>
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-center gap-2 flex-wrap'>
                                <Typography
                                    sx={{ color: '#666cff', fontSize: '16px', fontWeight: 600, textDecoration: 'underline' }}
                                    className='line-clamp-1 cursor-pointer'
                                    onClick={() => onViewDetail && onViewDetail(row.original)}
                                >
                                    {row.original.taskTitle || 'No Title'}
                                </Typography>
                                <Chip
                                    label={statusLabel}
                                    color={chipColor}
                                    variant='filled'
                                    size='small'
                                    className='font-medium text-[10px] h-[20px] px-2'
                                    sx={{
                                        color: '#fff !important',
                                        backgroundColor: (theme) => {
                                            if (chipColor === 'error') return '#ef4444';
                                            if (chipColor === 'success') return '#22c55e';
                                            if (chipColor === 'warning') return '#f59e0b';
                                            if (chipColor === 'info') return '#3b82f6';
                                            return undefined;
                                        }
                                    }}
                                />
                                {row.original.outcomeName && (
                                    <Typography sx={{ fontSize: '12.5px', color: '#22c55e', fontWeight: 600 }}>
                                        Outcome: {row.original.outcomeName}
                                    </Typography>
                                )}
                                {row.original.completedDate && (
                                    <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                                        Completed: {formatDate(row.original.completedDate, false)}
                                    </Typography>
                                )}
                                {row.original.conversionAmount && (
                                    <Typography sx={{ fontSize: '12.5px', color: '#4f46e5', fontWeight: 600 }}>
                                        Amount: {row.original.conversionAmount}
                                    </Typography>
                                )}
                                {row.original.convertedOn && (
                                    <Typography sx={{ fontSize: '12.5px', color: '#64748b', fontWeight: 600 }}>
                                        Converted On: {formatDate(row.original.convertedOn, false)}
                                    </Typography>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }
        }),
        columnHelper.accessor('startDate', {
            header: 'DATE',
            cell: ({ row }) => (
                <Typography className='font-medium' sx={{ fontSize: '14px', fontWeight: 600, color: '#334155 !important' }}>
                    {formatDate(row.original.startDate, false)}
                </Typography>
            )
        }),
        columnHelper.accessor('phone', {
            header: 'CONTACT DETAILS',
            cell: ({ row }) => {
                // Show phone exactly as returned from API — do NOT append any hardcoded country code
                const rawPhone = row.original.CsvMobileNo || row.original.phone || row.original.MobileNo || '';
                const displayPhone = rawPhone || '-';

                return (
                    <div className='flex items-center gap-2'>
                        <Tooltip title="Call">
                            <IconButton
                                size='small'
                                sx={{ color: '#28a745', '&:hover': { bgcolor: 'rgba(40, 167, 69, 0.08)' }, p: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAction && onAction(e, row.original, 'call');
                                }}
                            >
                                <Icon icon="ri:phone-fill" fontSize={22} />
                            </IconButton>
                        </Tooltip>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1e293b', opacity: 0.8 }}>
                            {displayPhone}
                        </Typography>
                    </div>
                )
            }
        }),
        columnHelper.accessor('name', {
            header: 'RELATED TO',
            cell: ({ row }) => (
                <div className='flex items-center gap-3'>
                    <ScoreCard
                        leadId={row.original.leadId}
                        mobileNo={row.original.phone}
                        personName={row.original.name}
                    >
                        <div className='flex-shrink-0' style={{ cursor: 'pointer' }}>
                            {getAvatar({ avatar: row.original.avatar, name: row.original.name, threshold: row.original.threshold })}
                        </div>
                    </ScoreCard>
                    <div className='flex flex-col gap-0.5'>
                        <div className='flex items-center gap-2'>
                            <Typography
                                className='cursor-pointer'
                                sx={{ color: '#666cff !important', fontSize: '15px', fontWeight: 600, margin: 0, textDecoration: 'underline' }}
                                onClick={() => onViewLead && onViewLead(row.original.leadId)}
                            >
                                {row.original.name}
                            </Typography>
                            <LaunchIcon
                                sx={{
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    color: '#94a3b8',
                                    '&:hover': { color: '#666cff' }
                                }}
                                onClick={(e) => { e.stopPropagation(); setSelectedEntity(row.original); setShowEntityEdit(true); }}
                                titleAccess="Edit Entity"
                            />
                        </div>
                    </div>
                </div>
            )
        }),
        columnHelper.accessor('creatorName', {
            header: 'AGENT NAME',
            cell: ({ row }) => (
                <div className='flex items-center gap-2'>
                    {getAgentAvatar(row.original.creatorAvatar, row.original.creatorName)}
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 600, color: '#333155 !important' }}>
                        {row.original.creatorName || '-'}
                    </Typography>
                </div>
            )
        }),
        columnHelper.accessor('action', {
            header: 'ACTION',
            cell: ({ row }) => {
                const settingActions = [
                    {
                        text: 'View Details',
                        icon: <Icon icon='tabler:eye' fontSize={20} style={{ color: '#3b82f6' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'view') }
                    }
                ];

                const allCrmActions = [
                    {
                        id: 'add_followup',
                        text: 'Add Followup',
                        icon: <Icon icon="ri:user-follow-line" fontSize={20} style={{ color: '#4caf50' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'add_followup') }
                    },
                    {
                        id: 'send-mail',
                        text: 'Send Mail',
                        icon: <Icon icon="ri:mail-send-line" fontSize={20} style={{ color: '#f44336' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'send-mail') }
                    },
                    {
                        id: 'send-sms',
                        text: 'Send SMS',
                        icon: <Icon icon="ri:chat-1-line" fontSize={20} style={{ color: '#2196f3' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'send-sms') }
                    },
                    {
                        id: 'send-voice',
                        text: 'Send Voice',
                        icon: <Icon icon="ri:mic-line" fontSize={20} style={{ color: '#9c27b0' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'send-voice') }
                    },
                    {
                        id: 'add-notes',
                        text: 'Add Note',
                        icon: <Icon icon="ri:sticky-note-line" fontSize={20} style={{ color: '#ff9800' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'add-notes') }
                    },
                    {
                        id: 'upload-document',
                        text: 'Upload Document',
                        icon: <Icon icon="ri:upload-cloud-2-line" fontSize={20} style={{ color: '#00bcd4' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'upload-document') }
                    },
                    {
                        id: 'addtask',
                        text: 'Add Task',
                        icon: <Icon icon="ri:task-line" fontSize={20} style={{ color: '#3f51b5' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'add-task') }
                    },
                    {
                        id: 'addappointment',
                        text: 'Add Appointment',
                        icon: <Icon icon="ri:calendar-event-line" fontSize={20} style={{ color: '#e91e63' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'add-appointment') }
                    },
                    {
                        id: 'adddeal',
                        text: 'Add Deal',
                        icon: <Icon icon="ri:hand-coin-line" fontSize={20} style={{ color: '#8bc34a' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'add-deal') }
                    },
                    {
                        id: 'fill-webform',
                        text: 'Fill Webform',
                        icon: <Icon icon="ri:article-line" fontSize={20} style={{ color: '#607d8b' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'fill-webform') }
                    }
                ];

                const crmActions = allCrmActions.filter(action => {
                    if (!crmOperationPerms) return true;
                    switch (action.id) {
                        case 'add_followup': return crmOperationPerms.AddFollowup !== false;
                        case 'send-mail': return crmOperationPerms.SendMail !== false;
                        case 'send-sms': return crmOperationPerms.SendSms !== false;
                        case 'send-voice': return crmOperationPerms.SendVoice !== false;
                        case 'add-notes': return crmOperationPerms.AddNote !== false;
                        case 'upload-document': return crmOperationPerms.UploadDocument !== false;
                        case 'addtask': return crmOperationPerms.AddTask !== false;
                        case 'addappointment': return crmOperationPerms.AddAppointment !== false;
                        case 'adddeal': return crmOperationPerms.AddDeal !== false;
                        case 'fill-webform': return crmOperationPerms.AddWebform !== false;
                        default: return true;
                    }
                });

                const isCompleted = row.original.taskStatus?.toLowerCase() === 'completed';

                if (!isCompleted) {
                    if (crmOperationPerms?.Edit) {
                        settingActions.push({ text: 'Edit', icon: <Icon icon="tabler:edit" fontSize={20} style={{ color: '#6366f1' }} />, menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => { e.stopPropagation(); setSelectedEntity(row.original); setShowEntityEdit(true); } } });
                    }
                }
                
                if (!isCompleted && crmOperationPerms?.Delete) {
                    settingActions.push({ text: 'Delete', icon: <Icon icon="tabler:trash" fontSize={20} style={{ color: '#ef4444' }} />, menuItemProps: { className: 'flex items-center gap-2', onClick: (e) => onAction && onAction(e, row.original, 'delete') } });
                }

                return (
                    <div className='flex items-center justify-center gap-2 w-full min-w-[100px]'>
                        <Tooltip title="Add Followup">
                            <IconButton
                                sx={{
                                    padding: '6px',
                                    color: '#666cff',
                                    bgcolor: 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(102, 108, 255, 0.08)' }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAction && onAction(e, row.original, 'add_followup_premium');
                                }}
                            >
                                <Icon icon="ri:add-fill" fontSize={26} />
                            </IconButton>
                        </Tooltip>
                        <OptionMenu
                            iconClassName='text-textSecondary'
                            icon={<Icon icon='tabler:settings' fontSize={20} style={{ color: '#64748b' }} />}
                            iconButtonProps={{
                                sx: {
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    padding: '7px',
                                    '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#cbd5e1' }
                                }
                            }}
                            options={crmActions}
                        />
                    </div>
                );
            },
            enableSorting: false
        })
    ], [onViewDetail, onViewLead, onAction, crmOperationPerms]);

    const hasMassPerms = crmOperationPerms && (
        crmOperationPerms.MassUpdate || crmOperationPerms.MassDelete || 
        crmOperationPerms.MassSMS || crmOperationPerms.MassEmail || 
        crmOperationPerms.MassAssign || crmOperationPerms.MassBroadcast
    );

    const filteredColumns = useMemo(() => {
        const withMassPerms = hasMassPerms ? columns : columns.filter(c => c.id !== 'select');
        return withMassPerms.filter(col => {
            if (col.id === 'taskTitle') return !isHiddenField('Subject');
            if (col.id === 'startDate') return !isHiddenField('StartDate');
            if (col.id === 'name') return !isHiddenField('PersonName');
            if (col.id === 'creatorName') return !isHiddenField('Owner');
            return true;
        });
    }, [columns, hasMassPerms, isHiddenField]);

    // Data is provided by FollowUpModule via props
    const filteredData = data || [];


    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minHeight: 0 }}>
            <PremiumModuleWrapper
                moduleName="Follow Up"
                stats={stats}
                onStatusClick={handleStatusFilter}
                loading={loading}
                isInitialLoad={isInitialLoad}
                data={filteredData}
                columns={filteredColumns}
                totalRecords={totalRecords}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={onPageChange}
                showCards={false}
                globalFilter={searchTerm}
            >
            </PremiumModuleWrapper>
            {/* 
            {showEntityEdit && (
                <EntityEdit
                    editingEntity={selectedEntity}
                    onCloseEntity={() => setShowEntityEdit(false)}
                />
            )} 
            */}
            {showEntityEdit && (
                <div className="entity-edit-overlay fixed inset-0 z-[9999] bg-white">
                    <EntityEdit
                        entityData={selectedEntity}
                        onSave={handleEntitySave}
                        onCancel={() => setShowEntityEdit(false)}
                        isLoading={isSavingEntity}
                    />
                </div>
            )}
        </Box>
    )
}

export default FollowUpListWrapper
